import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getLogs } from '../api/admin';
import Pagination from '../components/Pagination';
import styles from './Logs.module.css';

const ACTION_OPTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'approve_vehicle_owner_application', label: 'Approve Vehicle App' },
  { value: 'reject_vehicle_owner_application', label: 'Reject Vehicle App' },
  { value: 'block_user', label: 'Block User' },
  { value: 'unblock_user', label: 'Unblock User' },
  { value: 'bulk_delete_applications', label: 'Bulk Delete Apps' },
  { value: 'bulk_delete_users', label: 'Bulk Delete Users' },
  { value: 'approve_identity_verification', label: 'Approve ID Verif' },
  { value: 'reject_identity_verification', label: 'Reject ID Verif' },
];

const SORT_OPTIONS = [
  { value: 'created_at_desc', label: 'Newest First' },
  { value: 'created_at_asc', label: 'Oldest First' },
];

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function Logs() {
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at_desc');
  const [search, setSearch] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['logs', page],
    queryFn: () => getLogs({ page, limit: 20 }).then((r) => r.data),
    keepPreviousData: true,
  });

  let logsToShow = data?.logs || [];
  
  // Filter by action
  if (actionFilter) {
    logsToShow = logsToShow.filter(log => log.action === actionFilter);
  }

  // Filter by search
  if (search) {
    const lowerSearch = search.toLowerCase();
    logsToShow = logsToShow.filter(log => 
      (log.admin?.full_name || '').toLowerCase().includes(lowerSearch) ||
      (log.admin?.email || '').toLowerCase().includes(lowerSearch) ||
      (log.target?.full_name || '').toLowerCase().includes(lowerSearch) ||
      (log.target?.email || '').toLowerCase().includes(lowerSearch) ||
      log.action.toLowerCase().includes(lowerSearch)
    );
  }

  // Sort
  if (sortBy === 'created_at_desc') {
    logsToShow.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  } else if (sortBy === 'created_at_asc') {
    logsToShow.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  }

  const pagination = data?.pagination;

  return (
    <div>
      <h1 className={styles.pageTitle}>Activity Log</h1>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search admin, target, or action…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', minWidth: '250px'
          }}
        />
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          style={{
            padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', background: '#fff', cursor: 'pointer'
          }}
        >
          {ACTION_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={{
            padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', background: '#fff', cursor: 'pointer'
          }}
        >
          {SORT_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {isLoading && <p className={styles.msg}>Loading…</p>}
      {isError && <p className={styles.msgError}>Failed to load logs.</p>}

      {data && (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Admin</th>
                  <th>Action</th>
                  <th>Target User</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logsToShow.length === 0 && (
                  <tr>
                    <td colSpan={5} className={styles.empty}>No log entries.</td>
                  </tr>
                )}
                {logsToShow.map((log) => (
                  <tr key={log.id}>
                    <td className={styles.date}>{fmt(log.created_at)}</td>
                    <td>{log.admin?.full_name || log.admin?.email || '—'}</td>
                    <td><span className={styles.action}>{log.action.replace(/_/g, ' ')}</span></td>
                    <td>{log.target?.full_name || log.target?.email || '—'}</td>
                    <td>
                      {log.details ? (
                        <pre className={styles.details}>
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={pagination?.page || 1}
            pages={pagination?.pages || 1}
            onPage={setPage}
          />
        </>
      )}
    </div>
  );
}
